import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import client from "@/api/client";
import { Upload, Play, BookTemplate, Clock, FileSpreadsheet, ArrowRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 hover:-translate-y-0.5 transition-all duration-200 hover:shadow-md">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-widest text-gray-400 font-semibold">{label}</span>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center`} style={{ background: `${color}15` }}>
          <Icon size={16} style={{ color }} />
        </div>
      </div>
      <div className="text-3xl font-black text-gray-900">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      client.get("/files/"),
      client.get("/templates/"),
      client.get("/executions/"),
    ]).then(([uploadsRes, templatesRes, exeRes]) => {
      setUploads(uploadsRes.data.slice(0, 5));
      setTemplates(templatesRes.data.slice(0, 5));
      setExecutions(exeRes.data.slice(0, 5));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const statusColor = { completed: "#059669", running: "#006080", failed: "#DC2626", pending: "#D97706", paused: "#6B7280" };

  return (
    <div className="p-6 max-w-6xl mx-auto fade-in">
      {/* Welcome */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          Welcome back, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-gray-500 text-sm mt-0.5">Here's what's happening with your data pipelines.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Files Uploaded" value={uploads.length} icon={FileSpreadsheet} color="#006080" />
        <StatCard label="Templates" value={templates.length} icon={BookTemplate} color="#8B5CF6" />
        <StatCard label="Executions" value={executions.length} icon={Play} color="#059669" />
        <StatCard label="Active Runs" value={executions.filter(e => e.status === "running").length} icon={Clock} color="#D97706" />
      </div>

      {/* Quick actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
        <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-3">Quick Actions</p>
        <div className="flex flex-wrap gap-3">
          <Button
            className="bg-[#006080] hover:bg-[#004d66] text-white"
            onClick={() => navigate("/upload")}
            data-testid="quick-upload-btn"
          >
            <Upload size={15} className="mr-2" /> Upload File
          </Button>
          <Button
            variant="outline"
            className="border-[#006080] text-[#006080] hover:bg-[#e6f2f5]"
            onClick={() => navigate("/definitions")}
            data-testid="quick-api-def-btn"
          >
            <Plus size={15} className="mr-2" /> New API Definition
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/templates")}
            data-testid="quick-templates-btn"
          >
            <BookTemplate size={15} className="mr-2" /> Browse Templates
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent uploads */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Recent Files</p>
            <button className="text-xs text-[#006080] hover:underline" onClick={() => navigate("/upload")}>
              View all
            </button>
          </div>
          {loading ? (
            <div className="space-y-2">
              {[1,2,3].map(i => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}
            </div>
          ) : uploads.length === 0 ? (
            <div className="text-center py-8">
              <FileSpreadsheet size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">No files uploaded yet</p>
              <Button size="sm" className="mt-3 bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => navigate("/upload")}>
                Upload your first file
              </Button>
            </div>
          ) : (
            <div className="space-y-1">
              {uploads.map(u => (
                <button
                  key={u._id}
                  className="w-full flex items-center gap-3 p-2.5 rounded-md hover:bg-[#F8F9FA] text-left transition-colors"
                  onClick={() => navigate(`/staging/${u._id}`)}
                  data-testid={`upload-item-${u._id}`}
                >
                  <FileSpreadsheet size={16} className="text-[#006080] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{u.filename}</p>
                    <p className="text-xs text-gray-400">{u.row_count?.toLocaleString()} rows · {u.columns?.length} cols</p>
                  </div>
                  <ArrowRight size={14} className="text-gray-300" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recent executions */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Recent Executions</p>
            <button className="text-xs text-[#006080] hover:underline" onClick={() => navigate("/executions")}>
              View all
            </button>
          </div>
          {loading ? (
            <div className="space-y-2">
              {[1,2,3].map(i => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}
            </div>
          ) : executions.length === 0 ? (
            <div className="text-center py-8">
              <Play size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">No executions yet</p>
            </div>
          ) : (
            <div className="space-y-1">
              {executions.map(e => (
                <button
                  key={e._id}
                  className="w-full flex items-center gap-3 p-2.5 rounded-md hover:bg-[#F8F9FA] text-left transition-colors"
                  onClick={() => navigate(`/executions/${e._id}`)}
                  data-testid={`execution-item-${e._id}`}
                >
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: statusColor[e.status] || "#9CA3AF" }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      {e.dry_run ? "[Dry Run] " : ""}{e.processed_count}/{e.total_rows} rows
                    </p>
                    <p className="text-xs text-gray-400 capitalize">{e.status} · {e.success_count} ok, {e.error_count} failed</p>
                  </div>
                  <Badge
                    variant="secondary"
                    className="text-xs"
                    style={{ background: `${statusColor[e.status]}15`, color: statusColor[e.status] }}
                  >
                    {e.status}
                  </Badge>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
